import { Link } from 'react-router-dom';
import ArticleLayout from '@/components/shared/ArticleLayout';

const SalaryNegotiationGuide = () => (
  <ArticleLayout
    slug="salary-negotiation-guide"
    title="The Salary Negotiation Playbook: Using Government Data to Get Paid What You're Worth"
    description="Negotiation advice usually stops at 'do your research.' This guide shows what that research actually looks like: which numbers to pull, how to turn percentiles into a defensible ask, and the scripts that present data without sounding like an ultimatum."
    category="Career Advice"
    datePublished="2026-04-02"
    dateModified="2026-06-01"
    readTime="12 min read"
    faqItems={[
      {
        question: 'What salary data should I cite in a negotiation?',
        answer:
          'Use the BLS median and 75th percentile for your occupation in your state or metro as the anchor — it is official, employer-respected, and free of the self-selection bias of crowdsourced sites. Supplement it with current job postings for the same role to capture the new-hire market, which can run ahead of survey data.',
      },
      {
        question: 'Should I give a number first or wait for the employer?',
        answer:
          'If you have done the research, anchoring first usually works in your favor — name a specific figure near the top of your defensible range. If you have little information about the role\u2019s budget, deflect once ("I\u2019d like to understand the full scope first") and let them anchor, then negotiate from their number.',
      },
      {
        question: 'How much above the offer is it reasonable to counter?',
        answer:
          'For most professional roles, countering 8-15% above a reasonable offer is well within norms, especially with data behind it. The counter should land at a percentile you can justify: if you are a strong mid-career candidate and the offer sits at the median, a counter near the 75th percentile is a coherent, evidence-based position.',
      },
      {
        question: 'Does negotiating an offer risk getting it rescinded?',
        answer:
          'A respectful, data-grounded counter almost never costs you a written offer; rescissions are rare and usually involve ultimatums, repeated re-negotiation, or misrepresented competing offers. Asking once, with reasons, and accepting the outcome gracefully is treated as normal professional behavior.',
      },
    ]}
    relatedLinks={[
      {
        title: 'How to Read Salary Percentiles',
        description: 'The foundation for everything in this playbook — what p25, median, and p75 really mean.',
        href: '/insights/how-to-read-salary-percentiles',
      },
      {
        title: 'Cost of Living vs. Salary',
        description: 'Negotiating a relocation or remote role? Run the purchasing-power math first.',
        href: '/insights/cost-of-living-vs-salary',
      },
      {
        title: 'Understanding Wage Trends',
        description: 'Growth data is negotiation evidence: a rising market strengthens your ask.',
        href: '/insights/understanding-wage-trends',
      },
      {
        title: 'Search Salary Data',
        description: 'Pull the exact distribution for your occupation and state before the conversation.',
        href: '/search',
      },
    ]}
  >
    <p>
      Most negotiation advice is psychology with no ammunition: be confident, don't go first,
      practice in the mirror. All fine — and all secondary. Negotiations are won by the person whose
      number is harder to argue with, and the way to have that number is to walk in with data the
      other side already trusts. For salary conversations in the United States, that means the
      Bureau of Labor Statistics — the same wage survey, covering about 1.1 million establishments,
      that compensation departments themselves benchmark against.
    </p>
    <p>
      This playbook covers the whole sequence: what to pull, how to turn it into a range, what to
      say, and the special cases — relocations, remote roles, internal raises — where the standard
      advice breaks.
    </p>

    <h2>Step 1: Pull the distribution, not the average</h2>
    <p>
      Start at the <Link to="/search">salary search</Link> and open your occupation in the state
      (or metro) where the job sits. Ignore the single "average" — what you need is the full
      percentile spread: p10, p25, median, p75, p90. If you have not read our{' '}
      <Link to="/insights/how-to-read-salary-percentiles">percentile guide</Link>, read it first;
      the rest of this article assumes it.
    </p>
    <p>Three supplementary pulls strengthen the file:</p>
    <ul>
      <li>
        <strong>The industry breakdown</strong> on the same page. If your target employer's
        industry pays above the occupation's overall median, your benchmark moves up with it — and
        you can say so explicitly.
      </li>
      <li>
        <strong>The trend.</strong> Check the occupation's recent wage growth on the{' '}
        <Link to="/trends">trends page</Link>. "Wages for this role in this state grew X% over the
        past five years" is a strong sentence in a raise conversation, because it reframes your ask
        as keeping pace with the market rather than demanding a favor.
      </li>
      <li>
        <strong>Live postings.</strong> Survey data describes all incumbents, including people
        hired years ago. In hot markets, new-hire offers run ahead of it. Five to ten current
        postings for comparable roles give you the leading edge to pair with the official floor.
      </li>
    </ul>

    <h2>Step 2: Place yourself on the curve — honestly</h2>
    <p>
      Your ask must map to a percentile you can defend out loud. A useful calibration: entry-level
      with no specialization argues for p25-to-median; solid mid-career performance with in-demand
      skills argues for median-to-p75; deep specialization, scarce certifications, or a leadership
      track record argues for p75-to-p90. The test is whether you can complete this sentence
      without flinching: <em>"I'm benchmarking against the 75th percentile because…"</em> If
      nothing follows the "because," drop a band.
    </p>
    <p>
      Honesty here is tactical, not moral. An inflated self-placement gets dismantled by the first
      probing question, and with it your credibility for the rest of the conversation. A defensible
      placement, even a modest one, makes every subsequent number you say sound like analysis.
    </p>

    <h2>Step 3: Build the range</h2>
    <p>From your percentile band, construct three numbers:</p>
    <ul>
      <li>
        <strong>Walk-away:</strong> the figure below which the job stops making sense, after
        benefits and cost of living. Decide it before any conversation; it is much harder to think
        clearly mid-call.
      </li>
      <li>
        <strong>Target:</strong> the top of your defensible band. This is the number you actually
        say.
      </li>
      <li>
        <strong>Anchor:</strong> target plus roughly 5–10%, used when you speak first. Anchoring
        research is unambiguous: first numbers pull final outcomes toward themselves, and specific
        numbers ($117,500) anchor harder than round ones ($115,000) because they signal
        calculation.
      </li>
    </ul>
    <p>
      If the job is in a different metro than you live in now — or remote — run the{' '}
      <Link to="/insights/cost-of-living-vs-salary">cost-of-living adjustment</Link> on all three
      numbers. A range built on your current city's prices is the most common silent error in
      relocation negotiations.
    </p>

    <h2>Step 4: The scripts</h2>
    <p>
      The delivery principle is always the same: <strong>cite the data, state your placement, then
      stop talking.</strong> Data presented calmly invites discussion; data presented as an
      ultimatum invites defensiveness.
    </p>
    <p>
      <strong>When asked for expectations early:</strong> "Based on BLS data for this role in this
      state, and where my experience sits in that range, I'm targeting around $117,500 — but I'd
      like to learn the full scope of the role before settling on a number." You have anchored and
      stayed flexible in one sentence.
    </p>
    <p>
      <strong>Countering a written offer:</strong> "Thank you — I'm genuinely excited about this.
      Before I sign, I want to raise compensation once. The offer is at about the state median for
      this occupation; given [specific skill/experience], I believe the 75th percentile — around
      $X — is the right benchmark. Can we close that gap?" One ask, one reason, one number.
    </p>
    <p>
      <strong>Internal raise:</strong> "Since my last adjustment, market wages for my role in this
      state have grown X%, and my responsibilities have expanded to include [specifics]. I'd like
      my compensation to reflect the current market — the data puts that at $X." Pairing market
      movement with scope growth is the strongest internal framing, because it gives your manager a
      case they can repeat upward.
    </p>
    <p>
      <strong>If the base is truly fixed:</strong> negotiate adjacent terms in order of long-term
      value — the review date moved up six months with agreed criteria, the title (it compounds
      into every future benchmark), signing bonus, remote flexibility, education budget. A
      guaranteed early review at +8% is worth more than a one-time $5,000 bump.
    </p>

    <h2>The mistakes that cost real money</h2>
    <ul>
      <li>
        <strong>Citing only crowdsourced numbers.</strong> Self-reported sites skew toward
        higher-paid, higher-engagement workers, and employers know it. Use them as color, never as
        the spine of the argument — the spine is the federal survey.
      </li>
      <li>
        <strong>Negotiating against your own number.</strong> After you state a figure, silence is
        your tool. The classic error is filling the pause with "…but I'm flexible," which
        instantly converts your anchor into a ceiling.
      </li>
      <li>
        <strong>Comparing against the wrong geography.</strong> National medians are meaningless
        for an offer in a specific state. The state — and ideally metro — distribution is the only
        relevant benchmark, adjusted for local prices if you are relocating.
      </li>
      <li>
        <strong>Asking repeatedly.</strong> One well-evidenced counter is professionalism. A second
        round after they meet it reads as bad faith and is where rescission risk actually lives.
      </li>
      <li>
        <strong>Ignoring total compensation.</strong> A $5,000 base difference can be dwarfed by a
        retirement match, healthcare premiums, or bonus structure. Build a one-line spreadsheet
        before comparing offers; ten minutes prevents five-figure mistakes.
      </li>
    </ul>

    <h2>Why this works</h2>
    <p>
      Compensation managers spend their working lives looking at exactly the data described here.
      When a candidate cites the same sources, the conversation shifts from haggling to
      benchmarking — from "how much can I extract" to "what does the market say this work is
      worth." That shift is worth more than any individual tactic, because it lets the other side
      say yes without feeling they lost. The market did the arguing; you just brought the printout.
    </p>
  </ArticleLayout>
);

export default SalaryNegotiationGuide;
